import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNews, fetchCategories, fetchRecommendations, setCurrentCategory, setSearchTerm, fetchBookmarks,
  fetchNewsCountries, fetchNewsRegions, fetchUserCountry, setSelectedCountries, setSelectedRegion,
} from '../../redux/actions/newsAction';
import NewsCard from './components/NewsCard';
import CategoryFilter from './components/CategoryFilter';
import CountryRegionFilter from './components/CountryRegionFilter';
import SearchBar from './components/SearchBar';
import NewsRecommendations from './components/NewsRecommendations';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Pagination,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CategoryFilterSkeleton, NewsCardSkeleton, RecommendationsSkeleton } from './components/NewsSkeleton';
import { useNavigate } from 'react-router-dom';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FilterListIcon from '@mui/icons-material/FilterList';
import RecommendIcon from '@mui/icons-material/Recommend';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import TutorialModal from '../../components/TutorialModal';
import TutorialPinpoint from '../../components/TutorialPinpoint';

const NewsHubScreen = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [newsPage, setNewsPage] = useState(1);
  const [bookmarksPage, setBookmarksPage] = useState(1);
  const user = useSelector(state => state.auth?.user?.user);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [newsVideoModalOpen, setNewsVideoModalOpen] = useState(false);

  const pageSize = 10;
  const newsLanguage = ['ar', 'fr', 'en'].includes(i18n.language) ? i18n.language : 'en';

  const { news: rawNews, totalNews, bookmarks: rawBookmarks, totalBookmarks, categories, currentCategory, searchTerm, countries = [], regions = [], selectedCountries = [], selectedRegion = null, detectedCountry = null, loading, errors } = useSelector(state => state.legalNews);
  const selectedCountriesKey = selectedCountries.join(',');
  const news = Array.isArray(rawNews) ? rawNews : [];
  const bookmarks = Array.isArray(rawBookmarks) ? rawBookmarks : [];

  useEffect(() => {
    if (isMobile) setActiveTab('filter');
  }, [isMobile]);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchNewsCountries(newsLanguage));
    dispatch(fetchNewsRegions(newsLanguage));
    dispatch(fetchRecommendations());
    dispatch(fetchUserCountry());
    if (user) {
      loadBookmarks(1);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      setNewsPage(1);
      loadNews(1);
    } else if (activeTab === 'favorites') {
      setBookmarksPage(1);
      loadBookmarks(1);
    }
  }, [currentCategory, searchTerm, activeTab, newsLanguage, selectedCountriesKey, selectedRegion]);

  const loadNews = (page) => {
    dispatch(fetchNews({
      skip: (page - 1) * pageSize,
      limit: pageSize,
      category: currentCategory,
      country: selectedCountries.length > 0 ? selectedCountries : null,
      region: selectedRegion,
      search: searchTerm || null,
      language: newsLanguage,
    }));
  };

  const loadBookmarks = (page) => {
    dispatch(fetchBookmarks({
      skip: (page - 1) * pageSize,
      limit: pageSize,
      category: currentCategory,
      language: newsLanguage,
    }));
  };

  const handleNewsPageChange = (event, newPage) => {
    setNewsPage(newPage);
    loadNews(newPage);
    document.querySelector('[data-news-feed]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBookmarksPageChange = (event, newPage) => {
    setBookmarksPage(newPage);
    loadBookmarks(newPage);
    document.querySelector('[data-news-feed]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCategoryChange = (category) => {
    dispatch(setCurrentCategory(category));
    setShowFilters(false);
    setActiveTab('filter');
    setNewsPage(1);
    dispatch(fetchNews({
      skip: 0, limit: pageSize,
      category, search: searchTerm || null,
      country: selectedCountries.length > 0 ? selectedCountries : null,
      region: selectedRegion,
      language: newsLanguage,
    }));
  };

  const handleCountriesChange = (newCountries) => {
    dispatch(setSelectedCountries(newCountries));
    setNewsPage(1);
  };

  const handleRegionChange = (newRegion) => {
    dispatch(setSelectedRegion(newRegion));
    setNewsPage(1);
  };

  const handleSearch = (term) => dispatch(setSearchTerm(term));

  const handleTabChange = (tab) => {
    if (tab === 'filter') {
      if (activeTab === 'filter') setShowFilters(true);
      else { setActiveTab('filter'); setShowFilters(false); }
    } else if (tab === activeTab) {
      setActiveTab('all');
      setShowFilters(false);
    } else {
      setActiveTab(tab);
      setShowFilters(false);
    }
  };

  const totalNewsPages = Math.ceil(totalNews / pageSize);
  const totalBookmarksPages = Math.ceil(totalBookmarks / pageSize);

  const paginationSx = {
    '.MuiPaginationItem-root': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      borderRadius: '8px',
      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    },
    '.MuiPaginationItem-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
      '&:hover': { backgroundColor: theme.palette.primary.dark },
    },
  };

  const renderContent = () => {
    if (activeTab === 'favorites') {
      if (!user) {
        return (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            {t('news_hub.bookmarks.login_required')}
          </Alert>
        );
      }
      if (loading.bookmarks) {
        return <>{[...Array(3)].map((_, i) => <NewsCardSkeleton key={i} />)}</>;
      }
      if (bookmarks.length === 0) {
        return (
          <Box sx={{
            textAlign: 'center', py: 6,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
          }}>
            <InboxOutlinedIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {t('news_hub.bookmarks.empty')}
            </Typography>
          </Box>
        );
      }
      return (
        <>
          {bookmarks.map(item => <NewsCard key={item.id} news={item} />)}
          {totalBookmarksPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalBookmarksPages}
                page={bookmarksPage}
                onChange={handleBookmarksPageChange}
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
                sx={paginationSx}
              />
            </Box>
          )}
        </>
      );
    }

    if (loading.news) {
      return <>{[...Array(3)].map((_, i) => <NewsCardSkeleton key={i} />)}</>;
    }
    if (news.length === 0) {
      return (
        <Box sx={{
          textAlign: 'center', py: 6,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
        }}>
          <InboxOutlinedIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1.5 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t('news_hub.no_news')}
          </Typography>
        </Box>
      );
    }
    return (
      <>
        {news.map(item => <NewsCard key={item.id} news={item} />)}
        {totalNewsPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalNewsPages}
              page={newsPage}
              onChange={handleNewsPageChange}
              size={isMobile ? 'small' : 'medium'}
              showFirstButton
              showLastButton
              sx={paginationSx}
            />
          </Box>
        )}
      </>
    );
  };

  const mobileTabs = [
    { id: 'filter', icon: <FilterListIcon sx={{ fontSize: 16 }} />, label: currentCategory ? t(`news_category.${currentCategory}`) : t('news_category.all') },
    { id: 'recommendations', icon: <RecommendIcon sx={{ fontSize: 16 }} />, label: t('news_hub.recommended_for_you') },
    { id: 'favorites', icon: <BookmarkBorderIcon sx={{ fontSize: 16 }} />, label: t('news_hub.bookmarks.title') },
  ];

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 1 }, py: 2.2, width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, color: theme.palette.text.primary, fontFamily: theme.typography.h1?.fontFamily }}
          >
            {t('news_hub.title')}
          </Typography>
          <TutorialPinpoint storageKey="tutorial_pinpoint_news">
            <IconButton
              onClick={() => setNewsVideoModalOpen(true)}
              size="small"
              title={t('tutorial_video')}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </TutorialPinpoint>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <SearchBar onSearch={handleSearch} searchTerm={searchTerm} />

        {/* Mobile setup preferences */}
        <Box
          onClick={() => navigate('/legal-news/preferences')}
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            gap: 1.5,
            mt: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: '10px',
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            cursor: 'pointer',
          }}
        >
          <TuneIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.8rem' }}>
            {t('news_hub.setup_preferences.title')}
          </Typography>
        </Box>

        {/* Mobile action tabs */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            mt: 1.5,
            borderRadius: '10px',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {mobileTabs.map((tab) => {
            const isActive = activeTab === tab.id || (tab.id === 'filter' && showFilters);
            return (
              <Button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                startIcon={tab.icon}
                sx={{
                  flex: 1,
                  py: 1.25,
                  px: 1,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  minWidth: 0,
                  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.background.paper,
                  color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: isActive
                      ? theme.palette.primary.dark
                      : theme.palette.action.hover,
                  },
                  '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 }, '& svg': { fontSize: '16px !important' } },
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tab.label}
                </Box>
              </Button>
            );
          })}
        </Box>

        {/* Mobile filter panel */}
        {showFilters && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              mt: 1,
              p: 2,
              borderRadius: '10px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CountryRegionFilter
              countries={countries}
              regions={regions}
              selectedCountries={selectedCountries}
              selectedRegion={selectedRegion}
              onCountriesChange={handleCountriesChange}
              onRegionChange={handleRegionChange}
              detectedCountry={detectedCountry}
            />
            <Box sx={{ mt: 1.5 }} />
            {loading.categories ? <CategoryFilterSkeleton /> : (
              <CategoryFilter categories={categories} selectedCategory={currentCategory} onChange={handleCategoryChange} />
            )}
          </Box>
        )}

        {/* Mobile recommendations panel */}
        {activeTab === 'recommendations' && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              mt: 1.5,
              p: 2,
              borderRadius: '10px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {loading.recommendations ? <RecommendationsSkeleton /> : <NewsRecommendations />}
          </Box>
        )}
      </Box>

      {/* Main layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 2.5, alignItems: 'start' }}>
        {/* Sidebar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2 }}>
          {/* Setup Preferences */}
          <Box
            data-tour="news-preferences"
            onClick={() => navigate('/legal-news/preferences')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: '10px',
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.8rem', lineHeight: 1.3 }}>
                {t('news_hub.setup_preferences.title')}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', lineHeight: 1.3 }}>
                {t('news_hub.setup_preferences.description')}
              </Typography>
            </Box>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.5,
                color: theme.palette.text.disabled,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {t('news_hub.filters')}
            </Typography>

            {user && (
              <Button
                fullWidth
                variant={activeTab === 'favorites' ? 'contained' : 'outlined'}
                startIcon={activeTab === 'favorites' ? <BookmarkIcon sx={{ fontSize: 16 }} /> : <BookmarkBorderIcon sx={{ fontSize: 16 }} />}
                onClick={() => handleTabChange('favorites')}
                sx={{
                  mb: 1.5,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  borderRadius: '8px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                {t('news_hub.bookmarks.title')}
              </Button>
            )}

            <CountryRegionFilter
              countries={countries}
              regions={regions}
              selectedCountries={selectedCountries}
              selectedRegion={selectedRegion}
              onCountriesChange={handleCountriesChange}
              onRegionChange={handleRegionChange}
              detectedCountry={detectedCountry}
            />

            {loading.categories ? <CategoryFilterSkeleton /> : (
              <CategoryFilter categories={categories} selectedCategory={currentCategory} onChange={handleCategoryChange} />
            )}
          </Box>

          {/* Recommendations */}
          {user && (
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                p: 2,
              }}
            >
              {loading.recommendations ? <RecommendationsSkeleton /> : <NewsRecommendations />}
            </Box>
          )}
        </Box>

        {/* Feed */}
        <Box data-news-feed>
          {errors.news && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{errors.news}</Alert>
          )}
          {renderContent()}
        </Box>
      </Box>

      {/* Video tutorial modal */}
      <TutorialModal
        open={newsVideoModalOpen}
        onClose={() => setNewsVideoModalOpen(false)}
        defaultTab="news"
      />
    </Box>
  );
};

export default NewsHubScreen;
